import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services } from '../mock';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = services.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="bg-gray-950 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button className="bg-red-600 hover:bg-red-700">View All Services</Button>
          </Link>
        </div>
      </div>);

  }

  return (
    <div className="bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/90 to-gray-950/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <Link to="/services" className="inline-flex items-center text-gray-400 hover:text-red-500 transition-colors duration-200 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
          <h1 className="text-5xl md:text-6xl font-bold">{service.title}</h1>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {service.shortDesc}
              </p>

              {/* Capabilities */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Capabilities</h2>
                <div className="space-y-4">
                  {service.capabilities.map((capability, index) =>
                  <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-300 text-lg">{capability}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Why Choose Us</h2>
                <div className="space-y-4">
                  {service.benefits.map((benefit, index) =>
                  <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                      <p className="text-gray-300 text-lg">{benefit}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="bg-gray-900 border-gray-800 p-6 sticky top-24">
                <h3 className="!font-bold !text-2xl mb-6 !text-[#FFFFFF]">Request a Quote</h3>
                <p className="text-gray-400 mb-6">
                  Get a custom quote for your {service.title.toLowerCase()} project. Our team will respond within 24 hours.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-md font-semibold transition-all duration-300 mb-4">
                    Request Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-gray-800 py-3 rounded-md font-semibold">
                    Contact Us
                  </Button>
                </Link>
              </Card>

              {/* Related Services */}
              <div className="mt-8">
                <h3 className="text-xl font-bold mb-4">Other Services</h3>
                <div className="space-y-3">
                  {services.filter((s) => s.id !== serviceId).slice(0, 3).map((relatedService) =>
                  <Link
                    key={relatedService.id}
                    to={`/services/${relatedService.id}`}
                    className="block p-4 bg-gray-900 border border-gray-800 rounded-lg hover:border-red-600 transition-all duration-300">

                      <h4 className="font-semibold text-white mb-1">{relatedService.title}</h4>
                      <p className="text-sm text-gray-400 line-clamp-2">{relatedService.shortDesc}</p>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-lg text-gray-400 mb-6">
            Contact us today to discuss your precision machining requirements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold">
                Request a Quote
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 rounded-md font-semibold">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default ServiceDetail;